/**
 * Cache singleton des canvas offscreen pour le test de transparence pixel des blocs.
 * Clé : blocId (id du bloc, pas de l'instance).
 * Partagé entre BlockInstance (écriture) et CanvasViewer (lecture pour hit-test).
 */

interface AlphaEntry {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
}

const cache = new Map<number, AlphaEntry>();

export function useBlockAlphaCache() {
  function set(blocId: number, img: HTMLImageElement): void {
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (w === 0 || h === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    try {
      ctx.drawImage(img, 0, 0);
    } catch {
      return;
    }
    cache.set(blocId, { canvas, ctx, width: w, height: h });
  }

  function isOpaque(blocId: number, localX: number, localY: number): boolean | null {
    const entry = cache.get(blocId);
    if (!entry) return null; // pas encore chargé
    const px = Math.round(localX);
    const py = Math.round(localY);
    if (px < 0 || py < 0 || px >= entry.width || py >= entry.height) return false;
    try {
      const pixel = entry.ctx.getImageData(px, py, 1, 1).data;
      return pixel[3] > 10;
    } catch {
      return null;
    }
  }

  function invalidate(blocId: number): void {
    cache.delete(blocId);
  }

  return { set, isOpaque, invalidate };
}
